import React, { useEffect, useState } from "react";
import {
  TextField,
  InputAdornment,
  IconButton,
  Popover,
  Box,
  SvgIcon,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StaticDateTimePicker } from "@mui/x-date-pickers/StaticDateTimePicker";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import { LuCalendar } from "@/Components/Icons/Icons";
import dayjs from "dayjs";
import FormLabel from "./FormLabel";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

export default function CustomDateTimePicker({
  label = "",
  value: externalValue,
  onChange,
  sx = {},
  textFieldSx = {},
  width = "20%",
  height,
  inputSize = "small",
  disabled,
  gap,
  required,
  placeholder = "DD:MM:YYYY HH:MM", // ✅ default placeholder
  maxDateToToday=false
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [value, setValue] = useState();
  const [tempValue, setTempValue] = useState(dayjs().format());
  const [currentView, setCurrentView] = useState();

 useEffect(() => {
  if (!externalValue) return;

  // Try multiple formats for safety
  let normalized = dayjs(externalValue, "DD-MM-YYYY HH:mm:ss");
  if (!normalized.isValid()) normalized = dayjs(externalValue); // fallback for ISO

  if (normalized.isValid()) {
    setValue(normalized);
    setTempValue(normalized);
  }
}, [externalValue]);


  useEffect(() => {
    if (!anchorEl) return; // wait until popover is opened

    let observer;
    let cleanupFn = null;

    const attachClickHandler = (calendarContainer) => {

      const delegatedHandler = (event) => {
        const dayBtn = event.target.closest(".MuiPickersDay-root");
        if (!dayBtn || currentView !== "day") return;

        if (dayBtn.classList.contains("Mui-selected")) {
          setTimeout(() => setCurrentView("hours"), 100);
        }
      };

      calendarContainer.addEventListener("click", delegatedHandler);
      cleanupFn = () =>
        calendarContainer.removeEventListener("click", delegatedHandler);
    };

    const tryAttach = () => {
      const calendarContainer =
        document.querySelector(".MuiDateCalendar-root") ||
        document.querySelector(".MuiDayCalendar-root") ||
        document.querySelector(".MuiPickersCalendar-root");

      if (calendarContainer) {
        attachClickHandler(calendarContainer);
        if (observer) observer.disconnect();
      }
    };

    // 1️⃣ Try immediately
    tryAttach();

    // 2️⃣ If not yet available, observe DOM for when it appears
    observer = new MutationObserver(() => {
      tryAttach();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (observer) observer.disconnect();
      if (cleanupFn) cleanupFn();
    };
  }, [anchorEl, currentView]);

  // ✅ Always wrap externalValue in dayjs()
  const currentValue = externalValue
    ? dayjs(externalValue, "DD-MM-YYYY HH:mm:ss")
    : value;


  const formatted =
    value && value?.isValid() ? value?.format("DD-MMM-YYYY HH:mm") : dayjs().format("DD-MMM-YYYY HH:mm");

   

  const handleClose = () => setAnchorEl(null);

 const handleOpen = (event) => {
  const startValue = value?.isValid()
    ? value
    : currentValue?.isValid()
    ? currentValue
    : dayjs();

  setTempValue(startValue);
  setCurrentView("day");
  setAnchorEl(event.currentTarget);
};

  const handleDateChange = (newValue) => {
    setTempValue(newValue);

    if (currentView === "year") {
      setCurrentView("month");
      return;
    }
    if (currentView === "month") {
      setCurrentView("day");
      return;
    }
    if (currentView === "day") {
      // Force move to hours even if same day is clicked
      setCurrentView("hours");
      return;
    }
    if (currentView === "hours") {
      setCurrentView("minutes");
      return;
    }
    if (currentView === "minutes") {
      setValue(newValue);
      if (onChange) {
        const apiValue = dayjs(newValue).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
        onChange(apiValue, newValue);
      }
      handleClose();
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        display="flex"
        flexDirection="column"
        gap={label ? 1 : 0}
        width={width}
        height={height}
        sx={{ ...sx }}
      >
        <FormLabel label={label} gap={gap} required={required} />

        <Box onClick={handleOpen}>
          <TextField
            disabled={disabled}
            value={formatted}
            fullWidth
            placeholder="DD:MM:YYYY HH:MM:SS"
            readOnly
            size={inputSize}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton tabIndex={-1}>
                    <SvgIcon sx={{ color: "var(--gray-800)" }}>
                      <LuCalendar />
                    </SvgIcon>
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: height || 40,
                fontSize: "16px",
                borderRadius: "8px",
                bgcolor: "var(--gray-0)",
                cursor: "pointer",
                alignItems: "center",
                "& fieldset": { border: "1px solid var(--gray-300)" },
                "&:hover fieldset": { borderColor: "var(--gray-300)" },
                "&.Mui-focused fieldset": {
                  border: "1px solid",
                  borderColor: "var(--brand-600)",
                  boxShadow: "0 0 0 4px var(--brand-200)",
                },
                ...(Boolean(anchorEl) && {
                  borderColor: "var(--brand-600)",
                  boxShadow: "0 0 0 4px var(--brand-200)",
                  "& fieldset": {
                    border: "1px solid var(--brand-600) !important",
                  },
                }),
              },
              "& .MuiInputBase-input": {
                padding: "8px 12px",
                cursor: "pointer",
              },
              ...textFieldSx,
            }}
            variant="outlined"
          />
        </Box>

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          PaperProps={{
            sx: {
              border: "1px solid var(--gray-300)",
              borderRadius: "8px",
              boxShadow: "0px 4px 12px var(--gray-300)",
              p: 1,
            },
          }}
        >
          <StaticDateTimePicker
            value={tempValue} // ✅ always dayjs
            onChange={(newValue) => handleDateChange(dayjs(newValue))}
            placeholder="DD:MM:YYYY HH:MM:SS"
            openTo={currentView} // ✅ important
            onViewChange={(view) => {
              if (view === "day" && currentView === "day") {
                // 👇 If same day clicked, force move to time view
                setCurrentView("hours");
              } else {
                setCurrentView(view);
              }
            }}
            // views={["year", "month", "day", "hours", "minutes"]}
            views={["year", "month", "day", "hours", "minutes"]}
            viewRenderers={{
              hours: renderTimeViewClock,
              minutes: renderTimeViewClock,
              seconds: renderTimeViewClock,
            }}
            slotProps={{ actionBar: { actions: [] } }}
            maxDate={maxDateToToday ? dayjs() : dayjs().endOf("year")} // 👈 conditionally limit
            sx={{
              "& .MuiTabs-indicator": {
                backgroundColor: "var(--brand-600)",
                height: "2px",
              },
              "& .MuiClockPicker-root, & .MuiClock-root": { mt: 2 },
              "& .Mui-selected": {
                backgroundColor: "var(--brand-800) !important",
                color: "var(--gray-100)",
              },
              "& .MuiButtonBase-root.MuiIconButton-root": {
                color: "var(--gray-800)",
              },
              "& .MuiTypography-root": { fontWeight: 500 },
              "& .MuiPickersCalendarHeader-root": {
                borderBottom: "1px solid var(--gray-300)",
                px: 2,
                pt: 1,
                pb: 1.5,
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                "& .MuiPickersCalendarHeader-labelContainer": {
                  display: "block",
                  textAlign: "center",
                  position: "absolute",
                  left: "50%",
                  transform: "translateX(-50%)",
                  fontWeight: 600,
                  fontSize: "14px",
                },
                "& .MuiPickersCalendarHeader-switchViewButton": {
                  display: "none",
                },
                "& .MuiPickersArrowSwitcher-root": {
                  position: "absolute",
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  pointerEvents: "none",
                },
                "& .MuiIconButton-root": { pointerEvents: "auto", zIndex: 1 },
              },
              "& .MuiPickersLayout-actionBar": { display: "none" },
            }}
          />
        </Popover>
      </Box>
    </LocalizationProvider>
  );
}
